// Queries over the editor state that the Swift's ScriveningsSession answers: windows of a body for the editing rules,
// fragment coordinates, and a builder for commands that make several edits as one undo step.
import { ChangeSet, EditorSelection, type EditorState, type Text } from '@codemirror/state';
import { layoutOf } from './state.ts';
import type { Window } from './rules.ts';

/** The blocks of body `i` that [a, b) touches, with two lines either side, as a window for the editing rules (which
 *  never look past the lines they touch and the block they're in). `base` is the window's document offset. */
export function windowAround(state: EditorState, i: number, a: number, b: number): { w: Window; base: number } {
  const doc = state.doc;
  const layout = layoutOf(state);
  const bodyFrom = layout.from[i], bodyTo = layout.to[i];
  const firstLine = doc.lineAt(bodyFrom).number, lastLine = doc.lineAt(bodyTo).number;
  let start = doc.lineAt(Math.max(bodyFrom, Math.min(a, bodyTo)));
  while (start.number > firstLine && doc.line(start.number - 1).text.trim() !== '') start = doc.line(start.number - 1);
  for (let k = 0; k < 2 && start.number > firstLine; k++) start = doc.line(start.number - 1);
  let end = doc.lineAt(Math.min(bodyTo, Math.max(b, bodyFrom)));
  while (end.number < lastLine && doc.line(end.number + 1).text.trim() !== '') end = doc.line(end.number + 1);
  for (let k = 0; k < 2 && end.number < lastLine; k++) end = doc.line(end.number + 1);
  const base = Math.max(start.from, bodyFrom);
  const stop = Math.min(end.to, bodyTo);
  const t = doc.sliceString(base, stop);
  // Bounds outside the window mean "the body goes on" (the lines kept either side stop every scan first).
  return { w: { t, bodyFrom: base === bodyFrom ? 0 : -1, bodyTo: stop === bodyTo ? t.length : t.length + 1 }, base };
}

/** Body `i` whole, as a window. */
export function bodyWindow(state: EditorState, i: number): { w: Window; base: number } {
  const layout = layoutOf(state);
  const t = state.doc.sliceString(layout.from[i], layout.to[i]);
  return { w: { t, bodyFrom: 0, bodyTo: t.length }, base: layout.from[i] };
}

export const bodyText = (state: EditorState, i: number) => state.doc.sliceString(layoutOf(state).from[i], layoutOf(state).to[i]);

/** A position as (fragment, UTF-16 offset), or null in a divider. */
export function fragmentAt(state: EditorState, pos: number): { index: number; fragmentId: string; offset: number } | null {
  const layout = layoutOf(state);
  const i = layout.bodyAt(pos);
  if (i < 0) return null;
  return { index: i, fragmentId: layout.pieces[i].id, offset: Math.min(pos, layout.to[i]) - layout.from[i] };
}

/** Several edits made one after another (each in the text the ones before it left, as `replaceRun` calls are), as
 *  one change set: one transaction, one undo step. */
export class EditBuilder {
  doc: Text;
  changes: ChangeSet;
  constructor(readonly state: EditorState) {
    this.doc = state.doc;
    this.changes = ChangeSet.empty(state.doc.length);
  }
  replace(from: number, to: number, text: string) {
    const c = ChangeSet.of({ from, to, insert: text }, this.doc.length);
    this.doc = c.apply(this.doc);
    this.changes = this.changes.compose(c);
  }
  slice(from: number, to: number) { return this.doc.sliceString(from, to); }
  get length() { return this.doc.length; }
  get empty() { return this.changes.empty; }
}

export const cursor = (pos: number) => EditorSelection.cursor(pos);
